/**
 * Type-ahead ranking: keep the site's best-seller order (typeahead.sort =
 * custitem_rank:desc,relevance:desc) for word searches, but when the shopper
 * types something that looks like an item number, run a second, relevance-
 * sorted request in parallel and move matching items to the top:
 *
 *   1. item number (itemid) exactly equal to the query
 *   2. an Old Part Number (custitem_old_part_number) exactly equal to it
 *   3. item numbers that START with the query, in relevance order
 *   4. part-number-like Old Part Numbers that START with the query (4+ chars;
 *      see Utils.isPartNumberLike — descriptive values are excluded)
 *
 * then the best-seller list, de-duplicated, truncated to the type-ahead limit.
 * Comparison ignores case, spaces and punctuation, so "CSD-01386",
 * "csd01386" and "CSD 01386" all hit CC 01386's old number CSD01386.
 *
 * Mechanism: a jQuery ajaxTransport that claims only the header type-ahead
 * request (fieldset=typeahead with a digit-bearing q). It issues the original
 * request plus the relevance probe, merges the two item lists, and hands the
 * merged JSON back to jQuery as the response. Nothing in the SuiteCommerce
 * view or collection code is touched, and no private core module is
 * required (Safe Mode stays happy).
 *
 * Failure modes all fall back to today's behaviour: a failed or aborted probe
 * simply yields no boost; a failed main request fails exactly as before.
 */
define('Clemtex.Search.SkuRanking', [
    'Clemtex.Search.Utils',
    'jQuery',
    'underscore'
], function (Utils, jQuery, _) {
    'use strict';

    var PROBE_LIMIT = 6;
    var installed = false;

    function itemKey(item) {
        return String(item.internalid || item.itemid);
    }

    // 0 = exact item number, 1 = exact old part number, 2 = item-number prefix,
    // 3 = old-part-number prefix (part-number-like values only), null = no match
    function matchRank(item, normalizedQuery) {
        var itemNumber = Utils.normalize(item && item.itemid);
        if (itemNumber && itemNumber === normalizedQuery) { return 0; }
        if (Utils.matchingAltNumber(item, normalizedQuery)) { return 1; }
        if (itemNumber && itemNumber.indexOf(normalizedQuery) === 0) { return 2; }
        if (Utils.prefixAltNumber(item, normalizedQuery)) { return 3; }
        return null;
    }

    function merge(rankedItems, probeItems, query, limit) {
        var normalizedQuery = Utils.normalize(query);
        if (normalizedQuery.length < Utils.MIN_MATCH_LENGTH) { return rankedItems; }

        var boosted = _.filter(probeItems, function (item) {
            return matchRank(item, normalizedQuery) !== null;
        });
        // _.sortBy is stable, so relevance order survives within each group.
        boosted = _.sortBy(boosted, function (item) { return matchRank(item, normalizedQuery); });
        if (!boosted.length) { return rankedItems; }

        var seen = {};
        var merged = [];
        _.each(boosted.concat(rankedItems), function (item) {
            var key = itemKey(item);
            if (!seen[key]) {
                seen[key] = true;
                merged.push(item);
            }
        });
        return limit ? merged.slice(0, limit) : merged;
    }

    function shouldHandle(options) {
        if (options.clemtexPassthrough || (options.type || 'GET').toUpperCase() !== 'GET') { return null; }
        if (!Utils.isItemsApi(options.url)) { return null; }
        var params = Utils.parseUrl(options.url).params;   // URL is complete by transport time
        if (params.fieldset !== 'typeahead' || !params.q || !Utils.looksLikeItemNumber(params.q)) { return null; }
        if (Utils.normalize(params.q).length < Utils.MIN_MATCH_LENGTH) { return null; }
        return params;
    }

    function transport(options, originalOptions, jqXHR) {
        var params = shouldHandle(options);
        if (!params) { return; }   // decline: jQuery uses its normal transport

        var mainRequest, probeRequest;
        var aborted = false;

        return {
            send: function (headers, complete) {
                var parsed = Utils.parseUrl(options.url);
                var probeUrl = Utils.buildUrl(parsed.base, _.extend({}, parsed.params, {
                    sort: 'relevance:desc',
                    limit: PROBE_LIMIT,
                    offset: 0
                }));
                var common = {
                    type: 'GET',
                    dataType: 'json',
                    headers: headers,
                    cache: options.cache !== false,
                    clemtexPassthrough: true
                };

                var probeItems = [];
                var probeSettled = jQuery.Deferred();
                probeRequest = jQuery.ajax(_.extend({ url: probeUrl }, common));
                probeRequest.done(function (json) {
                    probeItems = (json && json.items) || [];
                }).always(function () {
                    probeSettled.resolve();
                });

                mainRequest = jQuery.ajax(_.extend({ url: options.url }, common));
                mainRequest.done(function (json, textStatus, xhr) {
                    probeSettled.done(function () {
                        if (aborted) { return; }
                        try {
                            json.items = merge(json.items || [], probeItems, params.q, parseInt(params.limit, 10) || 0);
                        } catch (e) {
                            if (window.console) { window.console.error('ClemtexSearch SkuRanking merge', e); }
                        }
                        complete(200, 'success', { text: JSON.stringify(json) }, xhr.getAllResponseHeaders());
                    });
                }).fail(function (xhr, textStatus) {
                    if (aborted) { return; }
                    complete(xhr.status || 500, textStatus || 'error', { text: xhr.responseText || '' },
                        xhr.getAllResponseHeaders ? xhr.getAllResponseHeaders() : '');
                });
            },
            abort: function () {
                aborted = true;
                try { if (mainRequest) { mainRequest.abort(); } } catch (e) {}
                try { if (probeRequest) { probeRequest.abort(); } } catch (e) {}
            }
        };
    }

    return {
        merge: merge,
        matchRank: matchRank,
        shouldHandle: shouldHandle,

        mountToApp: function mountToApp() {
            if (installed) { return; }
            installed = true;
            // "+*" puts this transport ahead of the defaults for every dataType;
            // it declines instantly for anything but the header type-ahead call.
            jQuery.ajaxTransport('+*', transport);
        }
    };
});
